import {
  Box,
  Button,
  Flex,
  HStack,
  NativeSelect,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { LuLogOut } from 'react-icons/lu'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from '../i18n'

import { ColorModeButton } from "./ui/color-mode"
import { APP_VERSION } from '../version'
import { LanguageSwitcher } from './LanguageSwitcher'

function NavLink({ to, label }) {
  const location = useLocation()
  const isActive = location.pathname.startsWith(to)
  return (
    <Button
      as={RouterLink}
      to={to}
      // Ghost, not solid: `solid` is for actions. The current page is marked
      // by brighter text over a 2px magenta rule — the "you are here" mark, the
      // one place magenta appears that is not the primary action.
      variant="ghost"
      size="sm"
      bg="transparent"
      color={isActive ? 'var(--mc-text)' : 'var(--mc-text-muted)'}
      borderWidth="0 0 2px 0"
      borderStyle="solid"
      borderBottomColor={isActive ? 'var(--mc-accent)' : 'transparent'}
      aria-current={isActive ? 'page' : undefined}
      _hover={{ color: 'var(--mc-text)', bg: 'var(--mc-bg-raised)' }}
      fontFamily="label"
      fontWeight="900"
      fontSize="xs"
      letterSpacing="0.09em"
      textTransform="uppercase"
    >
      {label}
    </Button>
  )
}

export default function Layout({ children }) {
  const { user, profile, isSysAdmin, isAdmin, eventRoles, logout } = useAuth()
  const { t } = useTranslation()

  // The badge under the email must say what the account actually is. A user
  // created in the webapp reaches this header with no role at all, and a
  // moderator is not an eventadmin — so read the roles rather than assuming
  // anyone who is not a sysadmin runs an event.
  const roleLabel = isSysAdmin
    ? t('common.sysadmin')
    : ([...new Set(eventRoles.map((r) => r.role).filter(Boolean))].join(' · ') || t('common.noRole'))

  return (
    <>
      {/* No background of its own: the body's glow and hairline grid
          (index.css) show through every page. */}
      <Flex direction="column" minH="100vh">
        {user && (
          <Flex
            as="header"
            px={{ base: 3, md: 6 }}
            py={3}
            gap={{ base: 1, md: 0 }}
            align="center"
            borderBottom="1px solid"
            borderColor="var(--mc-border)"
            bg="var(--mc-bg-header)"
            backdropFilter="blur(12px)"
            position="sticky"
            top={0}
            zIndex={10}
          >
            {/* Logo + ADMIN stacked vertically to keep the bar compact */}
            <VStack gap={0.5} align="flex-start" flexShrink={0} lineHeight="1">
              <img src="/CROWDS_logo-multicolor.svg" alt="CROWDS" width="104" style={{ display: 'block', height: 'auto' }} />
              <Text
                fontWeight="600"
                color="var(--mc-text-muted)"
                fontSize="2xs"
                letterSpacing="0.2em"
                textTransform="uppercase"
                lineHeight="1"
              >
                {t('common.admin')}
              </Text>
            </VStack>

            {/* The nav scrolls inside the header on a phone rather than widening
                the page: a document wider than the screen widens the phone's
                layout viewport, and every fixed dialog then centres itself in
                that wider box — partly offscreen. */}
            {!user.isAnonymous && isAdmin && (
              <HStack
                gap={2}
                ml={{ base: 2, md: 6 }}
                minW={0}
                overflowX="auto"
                css={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
              >
                <NavLink to="/events" label={t('nav.events')} />
                {isSysAdmin && !user.isAnonymous && <NavLink to="/event-types" label={t('nav.eventTypes')} />}
                {isSysAdmin && !user.isAnonymous && <NavLink to="/templates" label={t('nav.templates')} />}
                {isSysAdmin && !user.isAnonymous && <NavLink to="/users" label={t('nav.users')} />}
              </HStack>
            )}
            <Spacer />
            <HStack gap={{ base: 2, md: 3 }}>
              <LanguageSwitcher />
              {/* User identity — hidden on mobile to keep the header on one line */}
              <HStack gap={2} display={{ base: 'none', md: 'flex' }}>
                <Box textAlign="right">
                  <Text fontSize="sm" fontWeight="600" color="var(--mc-text)">
                    {!user.isAnonymous ? profile?.name || user.email : t('common.anonymous')}
                  </Text>
                  {!user.isAnonymous && (
                    <Text fontSize="xs" color="var(--mc-text-muted)" fontFamily="'JetBrains Mono', monospace">
                      {roleLabel}
                    </Text>
                  )}
                </Box>
              </HStack>
              <Button
                size="sm"
                variant="outline"
                onClick={logout}
                aria-label={t('common.logout')}
              >
                <LuLogOut />
                <Box as="span" display={{ base: 'none', md: 'inline' }}>{t('common.logout')}</Box>
              </Button>
            </HStack>
            {/* Color-mode toggle — hidden on mobile per design */}
            <Box display={{ base: 'none', md: 'inline-flex' }}>
              <ColorModeButton />
            </Box>
          </Flex>
        )}

        <Box as="main" flex="1" p={[4, 6, 8]}>
          <Box maxW="1400px" mx="auto">
            {(user && user.isAnonymous) ? (
              <Text fontSize="sm" fontWeight="semibold" color="var(--mc-steel)">
                {t('common.anonymousNotice')}
              </Text>
            ) : (
              children
            )}
          </Box>
        </Box>

        <Box as="footer" px={6} py={2} textAlign="center">
          <Text
            fontSize="xs"
            color="var(--mc-text-muted)"
            fontFamily="'JetBrains Mono', monospace"
            opacity={0.6}
          >
            {APP_VERSION}
          </Text>
        </Box>

      </Flex>
    </>
  )
}
